import type { DocMigration } from "../docMigration";

function rekeyOptionChoicesIfPresent(table: Record<string, any>, oldId: string, newId: string): void {
  if (table[oldId] === undefined) {
    return;
  }
  table[newId] = table[oldId];
  delete table[oldId];
}

/**
 * Converts instance option set IDs from UID-based format (instanceOptionSet_xxx)
 * to derived format (archetypeId:componentInstanceId).
 *
 * This migration ensures that existing docs with UID-based instance option set IDs
 * are updated to use the new derived ID format, which is predictable and parseable.
 *
 * Derived id shape must match `deriveInstanceOptionSetId` in `doc/options/instanceOptionSetId.ts`
 * (that module cannot be imported here per migration eslint rules).
 */
const migration: DocMigration = {
  id: "20260327_derived_instance_option_set_ids",
  description: "Convert instance option set IDs from UID to derived format",
  apply(json: any) {
    const optionSets = json.optionSets ?? {};
    const optionChoices = json.optionChoices ?? {};
    const presets = json.presets ?? {};

    for (const [archetypeId, optionSet] of Object.entries(optionSets)) {
      const instanceOptionSets = (optionSet as any).instanceOptionSets;
      if (!instanceOptionSets) {
        continue;
      }

      const newInstanceOptionSets: Record<string, any> = {};
      for (const [oldId, instance] of Object.entries(instanceOptionSets)) {
        const inst = instance as any;
        const componentInstanceId = inst.componentInstanceId;

        const newId = `${archetypeId}:${componentInstanceId}`;

        // Skip if already in derived format
        if (oldId === newId) {
          newInstanceOptionSets[oldId] = inst;
          continue;
        }

        newInstanceOptionSets[newId] = { ...inst, id: newId };

        rekeyOptionChoicesIfPresent(optionChoices, oldId, newId);
        for (const presetId of Object.keys(presets)) {
          const preset = presets[presetId];
          const presetChoices = preset?.optionChoices;
          if (presetChoices && typeof presetChoices === "object") {
            rekeyOptionChoicesIfPresent(presetChoices, oldId, newId);
          }
        }
      }
      (optionSet as any).instanceOptionSets = newInstanceOptionSets;
    }

    json.optionChoices = optionChoices;
    return json;
  },
};

export default migration;
